import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, MessageCircle, Video, TrendingUp, Settings, ExternalLink, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { logger } from '@/utils/logger';
import { isNativePlatform, getPlatform } from '@/utils/platform';

interface NotificationOnboardingScreenProps {
  userId: string;
  onComplete: () => void;
}

type PermissionState = 'loading' | 'not_determined' | 'granted' | 'denied';

export function NotificationOnboardingScreen({ userId, onComplete }: NotificationOnboardingScreenProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState>('loading');
  const { setNotificationOnboardingComplete, setNotificationOnboardingSkipped } = useNotificationStore();
  const { checkPermissions, registerNotifications } = usePushNotifications();

  // Check permission status on mount
  useEffect(() => {
    const checkPermissionStatus = async () => {
      if (!isNativePlatform()) {
        setPermissionState('not_determined');
        return;
      }

      try {
        const permStatus = await checkPermissions();
        logger.debug('🔔 [Onboarding] Permission status:', permStatus);
        
        if (permStatus === 'granted') {
          setPermissionState('granted');
        } else if (permStatus === 'denied') {
          setPermissionState('denied');
        } else {
          setPermissionState('not_determined');
        }
      } catch (error) {
        logger.error('🔔 [Onboarding] Error checking permissions:', error);
        setPermissionState('not_determined');
      }
    };

    checkPermissionStatus();
  }, [userId, checkPermissions]);

  // Recheck permissions when app regains focus (user might have enabled in settings)
  useEffect(() => {
    if (!isNativePlatform() || permissionState !== 'denied') {
      return;
    }

    const handleFocus = async () => {
      try {
        const permStatus = await checkPermissions();
        if (permStatus === 'granted') {
          setPermissionState('granted');
          setNotificationOnboardingComplete(true);
          await registerNotifications();
        }
      } catch (error) {
        logger.error('🔔 [Onboarding] Error rechecking permissions on focus:', error);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [permissionState, checkPermissions, registerNotifications, setNotificationOnboardingComplete]);

  const handleEnableNotifications = async () => {
    if (!isNativePlatform()) {
      setNotificationOnboardingComplete(true);
      onComplete();
      return;
    }

    setIsLoading(true);
    
    try {
      logger.debug('🔔 [Onboarding] Button clicked, requesting permission...');
      
      await registerNotifications();
      
      const permStatus = await checkPermissions();
      logger.debug('🔔 [Onboarding] Permission result:', permStatus);
      
      if (permStatus === 'granted') {
        setPermissionState('granted');
        setNotificationOnboardingComplete(true);
        // User will see success screen and click Continue to dismiss
      } else if (permStatus === 'denied') {
        setPermissionState('denied');
        setNotificationOnboardingComplete(false);
        // User will see denied screen and can choose to open settings or skip
      } else {
        // Edge case: permission still not determined after request
        // Dismiss screen to avoid showing the same request screen again
        setPermissionState('not_determined');
        setNotificationOnboardingComplete(false);
        onComplete();
      }
    } catch (error) {
      logger.error('🔔 [Onboarding] Error requesting permissions:', error);
      const permStatus = await checkPermissions();
      if (permStatus === 'denied') {
        setPermissionState('denied');
        setNotificationOnboardingComplete(false);
        // User will see denied screen and can choose action
      } else {
        // Edge case: error occurred, dismiss screen
        setPermissionState('not_determined');
        setNotificationOnboardingComplete(false);
        onComplete();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenSettings = async () => {
    if (!isNativePlatform()) {
      return;
    }

    try {
      logger.debug('🔔 [Onboarding] Opening app settings...');
      const platform = getPlatform();
      if (platform === 'ios') {
        window.open('app-settings:', '_system');
      } else if (platform === 'android') {
        window.open('app-settings:', '_system');
      }
    } catch (error) {
      logger.error('🔔 [Onboarding] Error opening settings:', error);
    }
  };

  const handleContinue = () => {
    console.log('🔔 [Onboarding] User clicked Continue on granted screen');
    setNotificationOnboardingComplete(true);
    onComplete();
  };

  const handleSkip = () => {
    console.log('🔔 [Onboarding] User clicked Skip');
    setNotificationOnboardingSkipped(true);
    onComplete();
  };

  const benefits = [
    {
      icon: MessageCircle,
      title: 'Recevez nos messages',
      description: 'Soyez notifié instantanément quand votre équipe vous envoie un message'
    },
    {
      icon: Video,
      title: 'Sortie de vos vidéos',
      description: 'Découvrez en premier quand vos nouvelles vidéos sont prêtes'
    },
    {
      icon: TrendingUp,
      title: 'Résultats & performances',
      description: 'Suivez les performances de vos contenus en temps réel'
    }
  ];

  // Loading state
  if (permissionState === 'loading') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      >
        <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
      </motion.div>
    );
  }

  // Granted state - show confirmation that notifications are enabled
  if (permissionState === 'granted') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center px-6"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-slow-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
        </div>

        <div className="relative z-10 max-w-md w-full flex flex-col items-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mb-8"
          >
            <CheckCircle2 className="w-12 h-12 text-green-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-foreground text-center mb-3"
          >
            Notifications activées
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-muted-foreground text-center mb-10"
          >
            Vous êtes prêt à recevoir vos notifications
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="w-full space-y-4 mb-10"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-2xl glass"
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-foreground font-medium mb-1">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="w-full"
          >
            <Button
              onClick={handleContinue}
              className="w-full h-14 bg-green-500 text-white hover:bg-green-600 font-semibold text-lg rounded-2xl transition-all duration-300"
            >
              Continuer
            </Button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Denied state - show "Go to Settings" UI
  if (permissionState === 'denied') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center px-6"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
        </div>

        <div className="relative z-10 max-w-md w-full flex flex-col items-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-8"
          >
            <Settings className="w-12 h-12 text-amber-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-foreground text-center mb-3"
          >
            Notifications désactivées
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-muted-foreground text-center mb-10"
          >
            Pour recevoir vos notifications, activez-les dans les réglages de votre iPhone
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="w-full p-4 rounded-2xl glass mb-8"
          >
            <p className="text-sm text-muted-foreground text-center">
              <span className="text-foreground font-medium">Réglages</span> → <span className="text-foreground font-medium">Prsnl</span> → <span className="text-foreground font-medium">Notifications</span> → Activer
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="w-full space-y-3"
          >
            <Button
              onClick={handleOpenSettings}
              className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg rounded-2xl transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                Ouvrir les réglages
              </span>
            </Button>

            <button
              onClick={handleSkip}
              className="w-full py-3 text-muted-foreground hover:text-foreground text-sm transition-colors"
            >
              Plus tard
            </button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Default state - show permission request UI
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center px-6"
    >
      {/* Background effects - same as MainAppLayout */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
      </div>

      <div className="relative z-10 max-w-md w-full flex flex-col items-center">
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-24 h-24 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-8"
        >
          <Bell className="w-12 h-12 text-foreground" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-foreground text-center mb-3"
        >
          Restez connecté
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-muted-foreground text-center mb-10"
        >
          Activez les notifications pour ne rien manquer
        </motion.p>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="w-full space-y-4 mb-10"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="flex items-start gap-4 p-4 rounded-2xl glass"
            >
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <benefit.icon className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="w-full space-y-3"
        >
          <Button
            onClick={handleEnableNotifications}
            disabled={isLoading}
            className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg rounded-2xl transition-all duration-300"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Activation...
              </span>
            ) : (
              'Activer mes notifications'
            )}
          </Button>

          <button
            onClick={handleSkip}
            className="w-full py-3 text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            Plus tard
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}
